import { useEffect, useRef, useState } from "react";
import {
  MapPin,
  Navigation,
  BatteryMedium,
  Wifi,
  RefreshCw,
} from "lucide-react";
import api from "../constant/api";
import { toast } from "react-toastify";

const tracker = {
  watchId: null,
  intervalId: null,
  active: false,
  userId: null,
  saveCallback: null,
};

export default function SalesmanLocation() {
  const [user, setUser] = useState(null);
  const [location, setLocation] = useState(null);
  const [address, setAddress] = useState("");
  const [battery, setBattery] = useState(null);
  const [networkType, setNetworkType] = useState("");
  const [loading, setLoading] = useState(false);
  const [tracking, setTracking] = useState(false);

  const batteryRef = useRef(null);
  const networkRef = useRef("");
  const userRef = useRef(null);
  const mountedRef = useRef(false);

  /* =========================================================
     LOAD USER
  ========================================================= */

  useEffect(() => {
    mountedRef.current = true;

    const savedUser = localStorage.getItem("user");

    if (savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);

        setUser(parsedUser);
        userRef.current = parsedUser;

        if (parsedUser?.user_id) {
          tracker.userId = parsedUser.user_id;
        }
      } catch (error) {
        console.log("User parse error:", error);
      }
    }

    getBattery();
    getNetwork();

    const active = localStorage.getItem("liveTrackingActive");

    if (active === "true") {
      setTracking(true);
    }

    /*
      IMPORTANT:
      Do NOT stop tracker here.

      Menu change:
      SalesmanLocation unmount
      ↓
      tracker continues running
      ↓
      location keeps updating
    */

    return () => {
      mountedRef.current = false;
    };
  }, []);

  /* =========================================================
     BATTERY
  ========================================================= */

  const getBattery = async () => {
    try {
      if (!navigator.getBattery) {
        return;
      }

      const batteryManager = await navigator.getBattery();

      const level = Math.round(batteryManager.level * 100);

      batteryRef.current = level;
      setBattery(level);

      const handleBatteryChange = () => {
        const newLevel = Math.round(
          batteryManager.level * 100
        );

        batteryRef.current = newLevel;
        setBattery(newLevel);
      };

      batteryManager.addEventListener(
        "levelchange",
        handleBatteryChange
      );
    } catch (error) {
      console.log("Battery error:", error);
    }
  };

  /* =========================================================
     NETWORK
  ========================================================= */

  const getNetwork = () => {
    try {
      const connection =
        navigator.connection ||
        navigator.mozConnection ||
        navigator.webkitConnection;

      if (connection) {
        const type = connection.effectiveType || "";

        networkRef.current = type;
        setNetworkType(type);
      }
    } catch (error) {
      console.log("Network error:", error);
    }
  };

  /* =========================================================
     ADDRESS
  ========================================================= */

  const getAddress = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`,
        {
          headers: {
            Accept: "application/json",
          },
        }
      );

      const data = await response.json();

      if (data?.display_name) {
        if (mountedRef.current) {
          setAddress(data.display_name);
        }

        return data.display_name;
      }

      return "";
    } catch (error) {
      console.log("Address error:", error);
      return "";
    }
  };

  // =====================================================
// UPDATE DATABASE LOCATION STATUS
// =====================================================

const updateLocationStatus = async (status) => {
  const currentUser =
    userRef.current || user;

  const currentUserId =
    tracker.userId || currentUser?.user_id;

  if (!currentUserId) {
    console.log("User ID missing");
    return false;
  }

  try {
    const response = await api.post(
      status === 1
        ? "/Salesman/startTracking"
        : "/Salesman/stopTracking",
      {
        user_id: currentUserId,
      }
    );

    console.log(
      "Location Status:",
      response.data
    );

    if (!response.data.success) {
      toast.error(response.data.msg);
      return false;
    }

    return true;

  } catch (error) {
    console.log(
      "Location Status Error:",
      error?.response?.data || error
    );

    toast.error(
      status === 1
        ? "Unable to start live tracking"
        : "Unable to stop live tracking"
    );

    return false;
  }
};
  /* =========================================================
     SAVE LOCATION
  ========================================================= */

  const saveLocation = async (position) => {
    const currentUser =
      userRef.current || user;

    const currentUserId =
      tracker.userId || currentUser?.user_id;

    if (!currentUserId) {
      console.log("User ID missing");
      return;
    }

    const latitude =
      position.coords.latitude;

    const longitude =
      position.coords.longitude;

    const accuracy =
      position.coords.accuracy || null;

    const speed =
      position.coords.speed || 0;

    const heading =
      position.coords.heading || 0;

    const currentLocation = {
      latitude,
      longitude,
      accuracy,
      speed,
      heading,
    };

    if (mountedRef.current) {
      setLocation(currentLocation);
    }

    const currentAddress =
      await getAddress(
        latitude,
        longitude
      );

    try {
      const payload = {
        user_id: currentUserId,
        latitude,
        longitude,
        accuracy,
        speed,
        heading,
        address: currentAddress || "",
        battery:
          batteryRef.current !== null
            ? batteryRef.current
            : null,
        network_type:
          networkRef.current || "",
      };

      console.log(
        "Location Payload:",
        payload
      );

      const response =
        await api.post(
          "/Salesman/saveLocation",
          payload
        );

      if (!response.data.success) {
        console.log(
          "Save location error:",
          response.data.msg
        );
      }
    } catch (error) {
      console.log(
        "Save location API error:",
        error?.response?.data ||
          error
      );
    }
  };

  /* =========================================================
     LOCATION ERROR
  ========================================================= */

  const locationError = (error) => {
    console.log(
      "GPS Error:",
      error
    );

    if (error.code === 1) {
      toast.error(
        "Location permission denied. Please allow location access."
      );
    } else if (error.code === 2) {
      toast.error(
        "Unable to get your location."
      );
    } else if (error.code === 3) {
      // toast.error(
      //   "Location request timed out."
      // );
    }
  };

  /* =========================================================
     START TRACKING
  ========================================================= */

 const startTracking = async () => {

  const currentUser =
    userRef.current || user;

  const currentUserId =
    currentUser?.user_id;

  if (!currentUserId) {
    toast.error("User login not found");
    return;
  }

  if (!navigator.geolocation) {
    toast.error(
      "Geolocation is not supported by this browser."
    );
    return;
  }

  if (tracker.active) {
    setTracking(true);
    return;
  }

  setLoading(true);

  // =====================================================
  // FIRST UPDATE DATABASE STATUS = 1
  // =====================================================

  const statusUpdated =
    await updateLocationStatus(1);

  if (!statusUpdated) {
    setLoading(false);
    return;
  }

  tracker.userId =
    currentUserId;

  tracker.active = true;

  tracker.saveCallback =
    saveLocation;

  localStorage.setItem(
    "liveTrackingActive",
    "true"
  );

  // =====================================================
  // GET FIRST LOCATION
  // =====================================================

  navigator.geolocation.getCurrentPosition(

    async (position) => {

      await saveLocation(position);

      if (mountedRef.current) {
        setLoading(false);
        setTracking(true);
      }

      toast.success(
        "Location tracking started ✅"
      );

      // =================================================
      // WATCH POSITION
      // =================================================

      if (tracker.watchId === null) {

        tracker.watchId =
          navigator.geolocation.watchPosition(

            async (position) => {

              if (
                tracker.active &&
                tracker.saveCallback
              ) {
                await tracker.saveCallback(
                  position
                );
              }

            },

            locationError,

            {
              enableHighAccuracy: true,
              maximumAge: 5000,
              timeout: 15000,
            }
          );
      }

      // =================================================
      // BACKUP 30 SECOND UPDATE
      // =================================================

      if (tracker.intervalId === null) {

        tracker.intervalId =
          setInterval(() => {

            if (!tracker.active) {
              return;
            }

            navigator.geolocation.getCurrentPosition(

              async (position) => {

                if (
                  tracker.active &&
                  tracker.saveCallback
                ) {
                  await tracker.saveCallback(
                    position
                  );
                }

              },

              locationError,

              {
                enableHighAccuracy: true,
                maximumAge: 0,
                timeout: 15000,
              }
            );

          }, 30 * 60 * 1000);
      }
    },

    async (error) => {

      // GPS failed
      tracker.active = false;
      tracker.saveCallback = null;

      localStorage.removeItem(
        "liveTrackingActive"
      );

      // IMPORTANT
      // GPS failed -> DB status back to 0

      await updateLocationStatus(0);

      if (mountedRef.current) {
        setLoading(false);
        setTracking(false);
      }

      locationError(error);
    },

    {
      enableHighAccuracy: true,
      maximumAge: 0,
      timeout: 15000,
    }
  );
};

  /* =========================================================
     STOP TRACKING
  ========================================================= */

 const stopTracking = async () => {

  const currentUser =
    userRef.current || user;

  const currentUserId =
    tracker.userId || currentUser?.user_id;

  if (!currentUserId) {
    toast.error("User login not found");
    return;
  }

  setLoading(true);

  // =====================================================
  // STOP DATABASE STATUS
  // =====================================================

  const statusUpdated =
    await updateLocationStatus(0);

  if (!statusUpdated) {
    setLoading(false);
    return;
  }

  // =====================================================
  // STOP FRONTEND TRACKER
  // =====================================================

  tracker.active = false;

  tracker.saveCallback = null;

  localStorage.removeItem(
    "liveTrackingActive"
  );

  // =====================================================
  // STOP WATCH POSITION
  // =====================================================

  if (tracker.watchId !== null) {

    navigator.geolocation.clearWatch(
      tracker.watchId
    );

    tracker.watchId = null;
  }

  // =====================================================
  // STOP 30 SECOND INTERVAL
  // =====================================================

  if (tracker.intervalId !== null) {

    clearInterval(
      tracker.intervalId
    );

    tracker.intervalId = null;
  }

  if (mountedRef.current) {
    setTracking(false);
    setLoading(false);
  }

  toast.success(
    "Live tracking stopped"
  );
};

  /* =========================================================
     REFRESH LOCATION
  ========================================================= */

  const refreshLocation = () => {
    if (!navigator.geolocation) {
      toast.error(
        "GPS not supported"
      );
      return;
    }

    setLoading(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        await saveLocation(
          position
        );

        setLoading(false);

        toast.success(
          "Location updated ✅"
        );
      },
      (error) => {
        setLoading(false);

        locationError(error);
      },
      {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 15000,
      }
    );
  };

  /* =========================================================
     AUTO RESTORE TRACKING
  ========================================================= */

  useEffect(() => {
    if (!user?.user_id) {
      return;
    }

    const active =
      localStorage.getItem(
        "liveTrackingActive"
      );

    if (
      active === "true" &&
      !tracker.active
    ) {
      const timer =
        setTimeout(() => {
          startTracking();
        }, 500);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [user]);

  /* =========================================================
     NO USER
  ========================================================= */

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white rounded-2xl shadow p-6 text-center">
          <MapPin
            size={40}
            className="mx-auto text-red-500 mb-3"
          />

          <h2 className="font-bold text-gray-800">
            User Login Required
          </h2>

          <p className="text-sm text-gray-500 mt-1">
            Please login again.
          </p>
        </div>
      </div>
    );
  }

  /* =========================================================
     UI
  ========================================================= */

  return (
    <div className="min-h-screen bg-gray-50 p-3 md:p-6">

      <div className="max-w-2xl mx-auto">

        {/* HEADER */}

        <div className="mb-5">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
            My Location
          </h1>

          <p className="text-sm text-gray-500 mt-1">
            Share your live location with admin
          </p>
        </div>

        {/* CARD */}

        <div className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden">

          {/* BLUE HEADER */}

          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white">

            <div className="flex items-center gap-4">

              <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                <MapPin size={28} />
              </div>

              <div>
                <h2 className="font-bold text-lg">
                  {user.name ||
                    "Salesman"}
                </h2>

                <p className="text-sm opacity-80">
                  {user.employee_code ||
                    user.email ||
                    ""}
                </p>
              </div>

            </div>

            {/* TRACKING STATUS */}

            <div className="mt-5 flex items-center gap-2">

              <span
                className={`w-3 h-3 rounded-full ${
                  tracking
                    ? "bg-green-400 animate-pulse"
                    : "bg-red-400"
                }`}
              />

              <span className="text-sm font-medium">
                {tracking
                  ? "Live Tracking Active"
                  : "Tracking Stopped"}
              </span>

            </div>

          </div>

          {/* BODY */}

          <div className="p-5">

            {/* LOCATION INFO */}

            <div className="grid grid-cols-2 gap-3">

              {/* LATITUDE */}

              <div className="bg-blue-50 rounded-2xl p-4">

                <div className="flex items-center gap-2 text-blue-600">

                  <Navigation size={16} />

                  <span className="text-xs font-medium">
                    Latitude
                  </span>

                </div>

                <p className="font-bold text-gray-800 mt-2 text-sm break-all">

                  {location
                    ? Number(
                        location.latitude
                      ).toFixed(7)
                    : "—"}

                </p>

              </div>

              {/* LONGITUDE */}

              <div className="bg-indigo-50 rounded-2xl p-4">

                <div className="flex items-center gap-2 text-indigo-600">

                  <Navigation size={16} />

                  <span className="text-xs font-medium">
                    Longitude
                  </span>

                </div>

                <p className="font-bold text-gray-800 mt-2 text-sm break-all">

                  {location
                    ? Number(
                        location.longitude
                      ).toFixed(7)
                    : "—"}

                </p>

              </div>

              {/* BATTERY */}

              <div className="bg-green-50 rounded-2xl p-4">

                <div className="flex items-center gap-2 text-green-600">

                  <BatteryMedium size={16} />

                  <span className="text-xs font-medium">
                    Battery
                  </span>

                </div>

                <p className="font-bold text-gray-800 mt-2">

                  {battery !== null
                    ? `${battery}%`
                    : "—"}

                </p>

              </div>

              {/* NETWORK */}

              <div className="bg-purple-50 rounded-2xl p-4">

                <div className="flex items-center gap-2 text-purple-600">

                  <Wifi size={16} />

                  <span className="text-xs font-medium">
                    Network
                  </span>

                </div>

                <p className="font-bold text-gray-800 mt-2 uppercase">

                  {networkType ||
                    "—"}

                </p>

              </div>

            </div>

            {/* ADDRESS */}

            {location && (
              <div className="mt-4 bg-gray-50 rounded-2xl p-4">

                <div className="flex items-start gap-3">

                  <MapPin
                    size={18}
                    className="text-red-500 mt-0.5"
                  />

                  <div>

                    <p className="text-xs text-gray-400">
                      Current Address
                    </p>

                    <p className="text-sm text-gray-700 mt-1 leading-5">
                      {address ||
                        "Getting address..."}
                    </p>

                  </div>

                </div>

              </div>
            )}

            {/* ACCURACY */}

            {location?.accuracy && (
              <div className="mt-3 text-xs text-gray-400 text-center">
                GPS Accuracy:{" "}
                {Math.round(
                  location.accuracy
                )}{" "}
                meters
              </div>
            )}

            {/* BUTTONS */}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">

              {!tracking ? (

                <button
                  onClick={
                    startTracking
                  }
                  disabled={loading}
                  className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3.5 rounded-2xl shadow-lg active:scale-95 transition disabled:opacity-50"
                >

                  <Navigation size={18} />

                  {loading
                    ? "Getting Location..."
                    : "Start Live Tracking"}

                </button>

              ) : (

                <button
                  onClick={
                    stopTracking
                  }
                  className="flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 text-white font-bold py-3.5 rounded-2xl shadow-lg active:scale-95 transition"
                >

                  <MapPin size={18} />

                  Stop Tracking

                </button>

              )}

              <button
                onClick={
                  refreshLocation
                }
                disabled={loading}
                className="flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-900 text-white font-bold py-3.5 rounded-2xl shadow active:scale-95 transition disabled:opacity-50"
              >

                <RefreshCw
                  size={18}
                  className={
                    loading
                      ? "animate-spin"
                      : ""
                  }
                />

                Refresh Location

              </button>

            </div>

            {/* INFO */}

            <div className="mt-5 bg-amber-50 border border-amber-100 rounded-2xl p-4">

              <p className="text-xs text-amber-700 leading-5">

                Live tracking sends your
                latest GPS location to the
                admin every 30 seconds while
                tracking is active.

                <br />

                You can move to another menu
                and return here without
                stopping the tracking.

              </p>

            </div>

          </div>

        </div>

      </div>

    </div>
  );
}